import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Phone, Mail, CheckCircle, RefreshCw, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_API_URL;

export default function EmailVerification() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [verified, setVerified] = useState(false);
  const [sessionToken, setSessionToken] = useState(null);
  const [email, setEmail] = useState("");
  const [manualToken, setManualToken] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    const token = localStorage.getItem("onboarding_session");
    const verificationToken = localStorage.getItem("email_verification_token");
    
    if (!token) {
      navigate("/pricing");
      return;
    }
    
    setSessionToken(token);
    
    // Get session info to display email
    fetchSessionInfo(token);
    
    // Check if token was passed in URL
    const urlToken = searchParams.get("token");
    if (urlToken) {
      verifyEmail(token, urlToken);
    } else if (verificationToken) {
      // Auto-verify if we have token stored (for demo purposes)
      setManualToken(verificationToken);
    }
  }, [navigate, searchParams]);

  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const fetchSessionInfo = async (token) => {
    try {
      const res = await axios.get(`${API}/api/v1/onboarding/session/${token}`);
      setEmail(res.data.email || res.data.qualification_data?.business_email || "");
    } catch (error) {
      console.error("Failed to fetch session:", error);
    }
  };

  const verifyEmail = async (sessToken, verToken) => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/api/v1/onboarding/verify-email/${sessToken}`, {
        verification_token: verToken
      });
      
      if (res.data.success) {
        setVerified(true);
        localStorage.removeItem("email_verification_token");
        toast.success("Email verified successfully!");
        
        // Auto-redirect after 2 seconds
        setTimeout(() => {
          navigate("/onboarding/checkout");
        }, 2000);
      }
    } catch (error) {
      const message = error.response?.data?.detail || "Verification failed";
      toast.error(message);
    }
    setLoading(false);
  };

  const handleManualVerify = () => {
    if (!manualToken.trim()) {
      toast.error("Please enter the verification code");
      return;
    }
    verifyEmail(sessionToken, manualToken.trim());
  };

  const handleResendEmail = async () => {
    if (resendCooldown > 0) return;
    
    setLoading(true);
    try {
      // In production, this would resend the email
      // For now, just show the token again
      const storedToken = localStorage.getItem("email_verification_token");
      if (storedToken) {
        toast.success("Verification code: " + storedToken.substring(0, 8) + "...");
      } else {
        toast.info("Check your email for the verification code");
      }
      setResendCooldown(60);
    } catch (error) {
      toast.error("Failed to resend email");
    }
    setLoading(false);
  };

  if (verified) {
    return (
      <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center">
        <Card className="bg-slate-900 border-slate-800 max-w-md w-full mx-4">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-400" />
            </div>
            <h2 className="text-2xl font-bold mb-2">Email Verified!</h2>
            <p className="text-slate-400 mb-6">
              Your email has been verified. Redirecting to payment...
            </p>
            <div className="animate-spin w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full mx-auto"></div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Header */}
      <div className="border-b border-slate-800 bg-slate-900/50">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Phone className="w-8 h-8 text-blue-500" />
            <span className="text-xl font-bold">Speakr</span>
          </div>
          <div className="flex items-center gap-4 text-sm text-slate-400">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center text-white text-sm">✓</div>
              <span className="hidden md:inline">Plan</span>
            </div>
            <div className="w-8 h-px bg-slate-700"></div>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center text-white text-sm">✓</div>
              <span className="hidden md:inline">Details</span>
            </div>
            <div className="w-8 h-px bg-slate-700"></div>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-white text-sm font-bold">3</div>
              <span className="hidden md:inline">Verify</span>
            </div>
            <div className="w-8 h-px bg-slate-700"></div>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center text-slate-400 text-sm">4</div>
              <span className="hidden md:inline text-slate-500">Payment</span>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-6 py-12 max-w-lg">
        <Card className="bg-slate-900 border-slate-800">
          <CardHeader className="text-center">
            <div className="w-16 h-16 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Mail className="w-8 h-8 text-blue-400" />
            </div>
            <CardTitle className="text-2xl">Check your email</CardTitle>
            <CardDescription className="text-slate-400">
              We sent a verification code to<br />
              <span className="text-white font-medium">{email || "your email"}</span>
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-6">
            {/* Manual code entry */}
            <div className="space-y-3">
              <label className="text-sm text-slate-400">Enter verification code</label>
              <div className="flex gap-2">
                <Input
                  value={manualToken}
                  onChange={(e) => setManualToken(e.target.value)}
                  placeholder="Paste your verification code"
                  className="bg-slate-800 border-slate-700 flex-1"
                  data-testid="verification-code-input"
                />
                <Button 
                  onClick={handleManualVerify}
                  disabled={loading || !manualToken.trim()}
                  className="bg-blue-500 hover:bg-blue-600"
                  data-testid="verify-button"
                >
                  {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : "Verify"}
                </Button>
              </div>
            </div>

            {/* Divider */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-700"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="bg-slate-900 px-2 text-slate-500">or</span>
              </div>
            </div>

            {/* Resend email */}
            <div className="text-center">
              <p className="text-sm text-slate-400 mb-3">
                Didn't receive the email? Check your spam folder or
              </p>
              <Button
                variant="outline"
                onClick={handleResendEmail}
                disabled={loading || resendCooldown > 0}
                className="border-slate-700 text-slate-300 hover:bg-slate-800"
              >
                {resendCooldown > 0 ? (
                  `Resend in ${resendCooldown}s`
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Resend verification email
                  </>
                )}
              </Button>
            </div>

            {/* Help text */}
            <div className="bg-slate-800/50 rounded-lg p-4 text-sm text-slate-400">
              <p className="font-medium text-slate-300 mb-1">Having trouble?</p>
              <p>
                Contact us at{" "}
                <a href="mailto:info@gskproductions.ca" className="text-blue-400 hover:underline">
                  info@gskproductions.ca
                </a>
                {" "}or call{" "}
                <a href="tel:+12893356542" className="text-blue-400 hover:underline">
                  +1 (289) 335-6542
                </a>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
